import { Box, IconButton, styled } from "@mui/material";
import { GlobalNetwork } from "@procore/core-icons";
import { quat, vec2 } from "gl-matrix";
import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { JOYSTICKS_LAYOUT, PROCORE_BOTTOM_ENTRY, TOOLBAR_LAYOUT, TOOLBAR_Z_INDEX } from "components";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useIsPhone } from "contexts/panelMode";
import { selectCameraType } from "features/render/renderSlice";
import { CameraType } from "features/render/types";
import { explorerActions } from "slices/explorer/slice";

import {
    ControlState,
    DEFAULT_CONTROL_STATE,
    handlePointerEnd,
    handlePointerMove,
    Orientation,
    useUpdateCamera,
    VISUAL_RADIUS,
} from "./handlers";

const QUAD_WIDTH = 94;
const BI_WIDTH = 64;
const HEIGHT = 94;

export function Joysticks() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const cameraType = useAppSelector(selectCameraType);
    const isPhone = useIsPhone();

    const dispatch = useAppDispatch();

    const [orientationMode, setOrientationMode] = useState(false);
    const [controlState, setControlState] = useState<ControlState>(DEFAULT_CONTROL_STATE);

    // Listen to pointer events
    useEffect(() => {
        const onPointerMove = (event: PointerEvent) => {
            setControlState((prev) => handlePointerMove(prev, event));
        };

        const onPointerUp = (event: PointerEvent) => {
            let didChange = false;
            setControlState((prev) => {
                const newState = handlePointerEnd(prev, event);
                didChange = newState !== prev;
                return newState;
            });
            if (didChange) {
                dispatch(explorerActions.setJoysticksMoving(false));
            }
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("pointercancel", onPointerUp);
        return () => {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
            window.removeEventListener("pointercancel", onPointerUp);
        };
    }, [dispatch]);

    // Track device orientation in orientation mode
    useEffect(() => {
        if (!orientationMode || !view) {
            return;
        }

        const handle = (event: DeviceOrientationEvent) => {
            setControlState((prev) => {
                const orientation: Orientation = {
                    alpha: event.alpha ?? 0,
                    beta: event.beta ?? 0,
                    gamma: event.gamma ?? 0,
                };

                if (prev.initialOrientation) {
                    return {
                        ...prev,
                        orientation,
                    };
                }

                // Some devices (Samsung tablet) sometimes report initial 0,0,0 orientation when it's not actually 0,0,0.
                // Skip it in this case and wait for the next event.
                const isDummyOrientation = orientation.alpha === 0 && orientation.beta === 0 && orientation.gamma === 0;
                if (isDummyOrientation) {
                    return prev;
                }

                return {
                    ...prev,
                    initialOrientation: orientation,
                    orientation,
                };
            });
        };

        window.addEventListener("deviceorientation", handle);
        return () => {
            window.removeEventListener("deviceorientation", handle);
        };
    }, [orientationMode, view]);

    const toggleOrientationMode = async () => {
        if (!view) {
            return;
        }

        const newValue = !orientationMode;
        if (newValue) {
            if ("requestPermission" in DeviceOrientationEvent) {
                try {
                    const permissionState = await (
                        DeviceOrientationEvent as { requestPermission: () => Promise<PermissionState> }
                    ).requestPermission();
                    if (permissionState !== "granted") {
                        return;
                    }
                } catch (error) {
                    console.error(error);
                    return;
                }
            }
            setControlState((prev) => ({
                ...prev,
                orientationInitialRotation: quat.clone(view.renderState.camera.rotation),
                initialOrientation: null,
                orientation: null,
            }));
        } else {
            setControlState((prev) => ({
                ...prev,
                orientation: null,
            }));
        }
        setOrientationMode(newValue);
    };

    useEffect(() => {
        if (!view) {
            return;
        }

        const handleOrientationChange = () => {
            setControlState((prev) => {
                if (!prev.initialOrientation || !prev.orientation) {
                    return prev;
                }

                return {
                    ...prev,
                    orientationInitialRotation: quat.clone(view.renderState.camera.rotation),
                    initialOrientation: prev.orientation,
                };
            });
        };

        screen.orientation.addEventListener("change", handleOrientationChange);
        return () => {
            screen.orientation.removeEventListener("change", handleOrientationChange);
        };
    }, [view]);

    useUpdateCamera(controlState);

    return (
        <Wrapper isPhone={isPhone}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    touchAction: "none",
                    userSelect: "none",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        gap: "20px",
                    }}
                >
                    <BiDirectionalJoystick
                        value={controlState.moveZ}
                        onPointerDown={(event) => {
                            event.preventDefault();
                            const { id, position } = getPointerIdAndPosition(event);
                            dispatch(explorerActions.setJoysticksMoving(true));
                            setControlState((state) => ({
                                ...state,
                                moveZPointerId: id,
                                moveZStartPos: position,
                            }));
                        }}
                    />
                    <QuadDirectionalJoystick
                        x={controlState.moveX}
                        y={controlState.moveY}
                        onPointerDown={(event) => {
                            event.preventDefault();
                            const { id, position } = getPointerIdAndPosition(event);
                            dispatch(explorerActions.setJoysticksMoving(true));
                            setControlState((state) => ({
                                ...state,
                                movePointerId: id,
                                moveStartPos: position,
                            }));
                        }}
                    />
                </Box>
                {cameraType === CameraType.Pinhole && (
                    <Box sx={{ position: "relative", marginRight: "-10px" }}>
                        {!orientationMode && <RightJoystickBackground />}
                        <OrientationModeSwitch active={orientationMode} onClick={toggleOrientationMode} />
                        {!orientationMode && (
                            <JoystickContainer
                                sx={{
                                    width: QUAD_WIDTH,
                                    height: HEIGHT,
                                    borderRadius: "24px",
                                    position: "absolute",
                                    top: 8,
                                    right: 12,
                                }}
                            >
                                <ControlKnob
                                    x={controlState.rotateX}
                                    y={controlState.rotateY}
                                    canMoveX
                                    onPointerDown={(event) => {
                                        event.preventDefault();
                                        const { id, position } = getPointerIdAndPosition(event);
                                        dispatch(explorerActions.setJoysticksMoving(true));
                                        setControlState((state) => ({
                                            ...state,
                                            rotatePointerId: id,
                                            rotateStartPos: position,
                                        }));
                                    }}
                                />
                            </JoystickContainer>
                        )}
                    </Box>
                )}
            </Box>
        </Wrapper>
    );
}

const Wrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isPhone",
})<{ isPhone?: boolean }>`
    flex: 0;
    position: relative;
    z-index: ${TOOLBAR_Z_INDEX.TOOLBAR + 1};
    margin-left: max(
        ${({ isPhone }) => (isPhone ? JOYSTICKS_LAYOUT.MARGIN_MOBILE : JOYSTICKS_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-left, 0px)
    );
    margin-right: max(
        ${({ isPhone }) => (isPhone ? JOYSTICKS_LAYOUT.MARGIN_MOBILE : JOYSTICKS_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-right, 0px)
    );
    margin-bottom: ${({ isPhone }) =>
        isPhone
            ? PROCORE_BOTTOM_ENTRY.TOOLBAR_HEIGHT + TOOLBAR_LAYOUT.MARGIN_MOBILE + JOYSTICKS_LAYOUT.MARGIN_BOTTOM_MOBILE
            : PROCORE_BOTTOM_ENTRY.TOOLBAR_HEIGHT +
              TOOLBAR_LAYOUT.MARGIN_TABLET +
              JOYSTICKS_LAYOUT.MARGIN_BOTTOM_TABLET_LANDSCAPE}px;
    display: flex;
    flex-direction: column;
    @media (orientation: portrait) and (min-width: ${JOYSTICKS_LAYOUT.MARGIN_BOTTOM_TABLET_PORTRAIT_MIN_WIDTH}px) {
        margin-bottom: ${PROCORE_BOTTOM_ENTRY.TOOLBAR_HEIGHT +
        TOOLBAR_LAYOUT.MARGIN_TABLET +
        JOYSTICKS_LAYOUT.MARGIN_BOTTOM_TABLET_PORTRAIT}px;
    }
`;

function OrientationModeSwitch({ active, onClick }: { active: boolean; onClick: () => void }) {
    return (
        <IconButton
            onClick={onClick}
            sx={{
                position: "absolute",
                top: 64,
                right: QUAD_WIDTH + 18,
                borderRadius: "11px",
                background: "rgba(0, 0, 0, 0.60)",
                boxShadow: "0 2px 6px 0 rgba(0, 0, 0, 0.30)",
                pointerEvents: "auto",
                display: "flex",
                width: "38px",
                height: "38px",
                padding: "8px",
                justifyContent: "center",
                alignItems: "center",
                color: "#E3E6E8",
                gap: "6px",
                ...(active && {
                    background: "#2066DF",
                    color: "#C6DBFF",
                    right: 12,
                    position: "relative",
                    top: "unset",
                }),
                "&:hover": {
                    background: active ? "color-mix(in hsl, #2066DF 95%, black)" : "rgba(0, 0, 0, 0.50)",
                },
                outline: active ? "4px solid #D7D6D633" : "none",
            }}
        >
            <GlobalNetwork size="tool" />
        </IconButton>
    );
}

function RightJoystickBackground() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="162"
            height="118"
            viewBox="0 0 162 118"
            fill="none"
            style={{
                // svg has some space in the bottom. Instead of redoing the path - just move it down.
                marginBottom: "-23px",
            }}
        >
            <g filter="url(#filter0_d_421_69)">
                <path
                    d="M129.033 4.06738C142.827 4.08571 154 15.2732 154 29.0674V80.5811C154 94.3956 142.795 105.592 128.98 105.581L66.5 105.533L20.9854 105.481C13.8115 105.473 8.00009 99.6553 8 92.4814V72.4668C8 65.2871 13.8203 59.4668 21 59.4668H38.5C43 59.4668 52.0998 56.5665 52.5 44.9668C52.646 40.7321 52.7075 35.2127 52.7188 29.5146C52.7467 15.4952 64.0294 3.98165 78.0488 4L129.033 4.06738Z"
                    fill="#D7D6D6"
                    fillOpacity="0.2"
                    shapeRendering="crispEdges"
                />
            </g>
            <defs>
                <filter
                    id="filter0_d_421_69"
                    x="0"
                    y="0"
                    width="162"
                    height="117.581"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="4" />
                    <feGaussianBlur stdDeviation="4" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_421_69" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_421_69" result="shape" />
                </filter>
            </defs>
        </svg>
    );
}

const JoystickContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "outline",
})<{ outline?: boolean }>(({ outline }) => ({
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: "50%",
    boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
    outline: outline ? "4px solid #D7D6D633" : "none",
}));

function QuadDirectionalJoystick({
    x,
    y,
    onPointerDown,
}: {
    x: number;
    y: number;
    onPointerDown: (event: React.PointerEvent<SVGSVGElement>) => void;
}) {
    return (
        <JoystickContainer outline sx={{ width: QUAD_WIDTH, height: HEIGHT, borderRadius: "24px" }}>
            <ControlKnob x={x} y={y} canMoveX onPointerDown={onPointerDown} />
        </JoystickContainer>
    );
}

function BiDirectionalJoystick({
    value,
    onPointerDown,
}: {
    value: number;
    onPointerDown: (event: React.PointerEvent<SVGSVGElement>) => void;
}) {
    return (
        <JoystickContainer outline sx={{ width: BI_WIDTH, height: HEIGHT, borderRadius: "20px" }}>
            <ControlKnob x={0} y={value} canMoveX={false} onPointerDown={onPointerDown} />
        </JoystickContainer>
    );
}

// TODO Safari (both desktop and mobile) has grey colors when you move joysticks around.
function ControlKnob({
    x,
    y,
    canMoveX,
    onPointerDown,
}: {
    x: number;
    y: number;
    canMoveX: boolean;
    onPointerDown: (event: React.PointerEvent<SVGSVGElement>) => void;
}) {
    const tx = x * VISUAL_RADIUS;
    const ty = y * VISUAL_RADIUS;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="84"
            height="86"
            viewBox="0 0 84 86"
            fill="none"
            style={{ overflow: "visible", willChange: "transform" }}
            onPointerDown={onPointerDown}
        >
            {/* Right arrow */}
            {canMoveX && (
                <path
                    d="M72 45.1424L72 38.8582C72 37.3426 73.6201 36.3777 74.9527 37.0997L80.7535 40.2424C82.15 40.999 82.1499 43.0032 80.7532 43.7596L74.9524 46.9011C73.6198 47.6228 72 46.6579 72 45.1424Z"
                    fill="#AAAAAA"
                    fillOpacity="0.5"
                />
            )}
            {/* Bottom arrow */}
            <path
                d="M38.8576 72L45.1418 72C46.6574 72 47.6223 73.6201 46.9003 74.9527L43.7576 80.7535C43.001 82.15 40.9968 82.1499 40.2405 80.7532L37.0989 74.9524C36.3772 73.6198 37.3421 72 38.8576 72Z"
                fill="#AAAAAA"
                fillOpacity="0.5"
            />
            {/* Left arrow */}
            {canMoveX && (
                <path
                    d="M12 45.1424L12 38.8582C12 37.3426 10.3799 36.3777 9.0473 37.0997L3.24648 40.2424C1.84996 40.999 1.85012 43.0032 3.24676 43.7596L9.04758 46.9011C10.3802 47.6228 12 46.6579 12 45.1424Z"
                    fill="#AAAAAA"
                    fillOpacity="0.5"
                />
            )}
            {/* Top arrow */}
            <path
                d="M38.8576 12L45.1418 12C46.6574 12 47.6223 10.3799 46.9003 9.0473L43.7576 3.24648C43.001 1.84996 40.9968 1.85012 40.2405 3.24676L37.0989 9.04757C36.3772 10.3802 37.3421 12 38.8576 12Z"
                fill="#AAAAAA"
                fillOpacity="0.5"
            />
            <g filter="url(#filter0_ddd_423_3675)" transform={`translate(${tx}, ${ty})`}>
                <circle cx="42" cy="42" r="22" transform="rotate(90 42 42)" fill="white" />
                <circle
                    opacity="0.8"
                    cx="42"
                    cy="42"
                    r="9.5"
                    transform="rotate(90 42 42)"
                    stroke="#ACB5B9"
                    strokeWidth="3"
                />
            </g>
            <defs>
                <filter id="filter0_ddd_423_3675" x="13" y="18" width="68" height="68" filterUnits="userSpaceOnUse">
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dx="5" dy="10" />
                    <feGaussianBlur stdDeviation="6" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                    <feBlend mode="normal" in2="BackgroundImage" result="effect1_dropShadow_423_3675" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_423_3675" result="shape" />
                </filter>
            </defs>
        </svg>
    );
}

function getPointerIdAndPosition(event: React.PointerEvent<SVGSVGElement>) {
    return {
        id: event.pointerId,
        position: vec2.fromValues(event.clientX, event.clientY),
    };
}
